<script lang="ts" context="module">
	declare global {
		interface Window {
			// eslint-disable-next-line @typescript-eslint/consistent-type-imports
			smartform: import("../../../core/schema/Smartform.js").ISmartform;
		}
	}
</script>

<script lang="ts">
	import type {
		SmartFormAddressResult,
		SmartFormCompanyResult,
		SmartFormInstance,
	} from "../../../core/schema/Smartform.js";
	import { createEventDispatcher, onMount } from "svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";

	const { SMARTFORM } = getGlobalSettings();
	const dispatch = createEventDispatcher<{
		companyValidation: SmartFormCompanyResult;
		addressValidation: SmartFormAddressResult;
	}>();

	// It's just super hard to know when the smartform instance is ready, so we have to wait for it. Also the company service is not enabled immediately it seems.
	async function waitForSmartformInstances(): Promise<SmartFormInstance[]> {
		return new Promise((resolve) => {
			const interval = setInterval(() => {
				const instances: SmartFormInstance[] = [];
				const instanceIds = window.smartform.getInstanceIds();

				if (instanceIds.length === 0) {
					const instance = window.smartform.getInstance();
					if (instance) {
						instances.push(instance);
					}
				} else {
					for (const id of instanceIds) {
						const instance = window.smartform.getInstance(id);
						if (instance) {
							instances.push(instance);
						}
					}
				}

				if (instances.length > 0 && window.smartform.isCompanyServiceEnabled()) {
					resolve(instances);
					clearInterval(interval);
				}
			});
		});
	}

	async function initializeSmartform(): Promise<void> {
		await new Promise((resolve) => {
			window.smartform.beforeInit = (): void => {
				window.smartform.setClientId(SMARTFORM);
				window.smartform.enableSetAutocomplete(true);
			};
			window.smartform.afterInit = resolve;
		});

		const smartformBody = document.querySelector("#smartform-body");

		function getSmartformInputs(): (HTMLInputElement | HTMLTextAreaElement)[] {
			return Array.from(
				smartformBody?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea") ?? [],
			).filter((input) => input.className.includes("smartform-"));
		}

		let currentSmartformInputs = getSmartformInputs();

		function addValidationCallback(): void {
			for (const instanceId of window.smartform.getInstanceIds()) {
				window.smartform.unbindForm(instanceId);
			}
			window.smartform.rebindAllForms(async () => {
				const instances = await waitForSmartformInstances();
				for (const instance of instances) {
					const instanceId = instance.getId();
					const instanceInputs = currentSmartformInputs.filter(({ className }) =>
						instanceId === "smartform-instance"
							? !className.includes("smartform-instance-")
							: className.includes(instanceId),
					);
					// eslint-disable-next-line no-loop-func, @typescript-eslint/no-loop-func
					instance.companyControl.addValidationCallback((result) => {
						dispatch("companyValidation", result);
						setTimeout(() => {
							for (const input of instanceInputs) {
								input.dispatchEvent(new Event("input", { bubbles: true }));
								input.dispatchEvent(new Event("change", { bubbles: true }));
								input.click(); // Trigger other validations (address) :trollface:
							}
						}, 250);
					});
					// eslint-disable-next-line no-loop-func, @typescript-eslint/no-loop-func
					instance.addressControl.addValidationCallback((result) => {
						dispatch("addressValidation", result);
						setTimeout(() => {
							for (const input of instanceInputs) {
								input.dispatchEvent(new Event("input", { bubbles: true }));
								input.dispatchEvent(new Event("change", { bubbles: true }));
								input.click(); // Trigger other validations (address) :trollface:
							}
						}, 250);
					});
				}
			});
		}

		addValidationCallback();

		// Listen for changes in the DOM, in case new smartform inputs are added
		const observer = new MutationObserver(() => {
			const newSmartformInputs = getSmartformInputs();
			const areInputsSame =
				currentSmartformInputs.length === newSmartformInputs.length &&
				currentSmartformInputs.every((input) => newSmartformInputs.includes(input));
			if (areInputsSame) {
				return;
			}
			currentSmartformInputs = newSmartformInputs;
			addValidationCallback();
		});

		if (smartformBody) {
			observer.observe(smartformBody, { childList: true, subtree: true });
		}
	}

	onMount(() => {
		const smartform = document.createElement("script");
		smartform.src = "https://client.smartform.cz/v2/smartform.js";
		smartform.addEventListener("load", () => {
			void initializeSmartform();
		});
		document.head.appendChild(smartform);
	});
</script>

<div role="none" id="smartform-body" class="contents">
	<slot />
</div>
